/**
 * UserMenu — account-level actions anchored to the right side of the TopBar.
 *
 * PR12: On mobile (<768px) this panel doubles as the "Account" bottom-sheet
 * reached from the mobile bottom-tab bar's Account slot. Row order (top-down):
 *   Profile → Settings → Custom logic → Exports → (Admin) → Sign out
 * This mirrors the N2=B decision: Profile is the top row inside the sheet,
 * above Settings / Sign out / Feedback.
 *
 * Design refs:
 *   design.md §6.1 — TopBar right region
 *   design.md §2   — one accent (open-state indicator only), hairlines not shadows,
 *                    Geist 500 for the button label, quiet 200ms motion
 *   design.md §14.2 — mobile Account bottom-sheet (PR12: 4-slot bottom-tab)
 *   branding.md §5, §5.2.1 — no banned words, no exclamations, precise voice
 *   branding.md §8  — vocabulary table
 *
 * Desktop (≥768px): first name + chevron-down, click opens a dropdown panel
 * anchored bottom-right of the trigger.
 * Mobile (<768px): circular user icon, tap opens a bottom sheet.
 *
 * Accent colour is ONLY used on the open-state trigger indicator, per the
 * one-accent rule in design.md §2. Menu chrome uses var(--line), var(--text),
 * var(--muted), var(--faint).
 *
 * Theme row removed 2026-05-08 (Frame 8 lock) — the L/D/S appearance toggle
 * lives in the Settings surface now. The useTheme import is retained on the
 * UserMenuProps contract (`onSetTheme`) so callers don't churn but the hook
 * is no longer mounted internally.
 *
 * Custom logic route: /app/settings/custom-logic — placeholder empty state,
 * Phase 5.
 *
 * Exports route: triggers /api/exports/* via fetch with credentials; falls back
 * gracefully when the route is unavailable (demo mode or pre-Phase-2 deploy).
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop verified.
 */

import { useState, useEffect, useRef } from "react";
import type { ThemeName } from "../../hooks/useTheme.js";
import { useIsAdmin } from "./useIsAdmin.js";
import { Sheet, SheetHeader, useIsDesktop } from "@proof/ui";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UserMenuProps {
  /**
   * The user's first name to display on desktop.
   *
   * Derivation (caller's responsibility):
   *   - Split `account.name` on the first space; take the leading part.
   *   - If `account.name` is null / empty, fall back to the portion of
   *     `account.email` before the `@` character.
   *   - e.g. "Edvard Kristiansen" → "Edvard"; "edvardk@outlook.com" → "edvardk".
   */
  firstName: string;
  /** The user's email address (used as fallback identity in the panel). */
  email: string;
  /**
   * PR12 (N2=B): Navigates to the public profile page (/u/<handle>).
   * Rendered as the TOP action row in both the desktop dropdown and the mobile
   * Account bottom-sheet — above Settings / Custom logic / Exports / Sign out.
   *
   * #221 Track D (d2): REQUIRED. Every route under the app shell must wire a
   * working Profile handler — a row that no-ops on the list route but works
   * inside a recipe was the d2 failure mode. The type system now forbids an
   * unwired Profile row from shipping. Callers always provide it (the route
   * decides whether to navigate to /u/<handle> or open the in-app editor).
   */
  onOpenProfile: () => void;
  /** Open the Settings view (same path as the ActivityBar gear icon). */
  onOpenSettings: () => void;
  /**
   * Navigate to the Groups surface (family-groups, plan 2026-07-02 §4.5).
   * Labelled primary-nav entry so Groups is discoverable on every breakpoint
   * (design.md §6.0a). Optional so existing fixtures/tests that don't wire it
   * simply omit the row; the authenticated shell always provides it.
   *
   * NAV-PLACEMENT NOTE (plan amendment 8): the user-menu entry + Library section
   * is the PROPOSED placement, pending instrument-design-reviewer sign-off.
   */
  onOpenGroups?: () => void;
  /**
   * Navigate to the Feed (/app/feed) — B-WP11b. Labelled primary-nav entry so
   * the Feed is discoverable on EVERY breakpoint: this is the <768px half of
   * the pair (the DesktopTopbar "Feed" control is the ≥768px half). Optional so
   * fixtures that don't wire it omit the row; the authenticated shell provides it.
   */
  onOpenFeed?: () => void;
  /**
   * Called when the user selects a theme from the theme sub-row.
   * Receives the new ThemeName. The caller may pass a no-op if it defers to
   * the internal useTheme hook (which is what the default wiring does).
   */
  onSetTheme: (theme: ThemeName) => void;
  /** Navigate to the Custom logic editor (placeholder route). */
  onOpenCustomLogic: () => void;
  /** Open the Exports panel. */
  onOpenExports: () => void;
  /**
   * Navigate to the Syntax help page (/app/help).
   *
   * REQUIRED — same rationale as onOpenFeedback: a top-level feature must have
   * a wired labelled entry on every breakpoint (design.md §6.0a, CLAUDE.md §11).
   * Making the prop required means an unwired "Syntax help" row cannot ship.
   *
   * Handler: navigate("/app/help") — same shape as onOpenCustomLogic.
   *
   * Plan: .plan/features/2026-06-12-help-syntax-page.md §D3
   */
  onOpenHelp: () => void;
  /**
   * Followup §3.7 — Open the Feedback modal.
   *
   * #221 Track D (d2): REQUIRED. Feedback is a top-level feature that must be
   * reachable from primary-nav on every breakpoint (CLAUDE.md §11 gotcha +
   * design.md §6.0a). The previous optional shape let the list route pass
   * `undefined`, so "Send feedback" silently no-op'd there while working inside
   * a recipe — the exact d2 bug. Making the prop required means an unwired
   * Feedback row can no longer ship; the shell single-sources one FeedbackModal
   * and every route opens it.
   */
  onOpenFeedback: () => void;
  /** Sign the user out. */
  onSignOut: () => void;
  /**
   * Navigate to the admin panel (/admin).
   * When absent the Admin row is not rendered (non-admin sessions never see it).
   * Callers should pass this only in the authenticated shell — the row is
   * additionally guarded by useIsAdmin() which suppresses it until admin.ping
   * confirms the session.
   */
  onOpenAdmin?: () => void;
  /**
   * P0 sign-out fix (2026-05-10): controlled-open mode for the desktop topbar
   * mount. When `open` is provided, the menu becomes a controlled component —
   * `open` drives panel/sheet visibility, and `onOpenChange` fires for every
   * state transition (trigger click, outside click, Escape, action-row click).
   * Callers that want internal-state behaviour (the legacy TopBar mount) leave
   * both `open` and `onOpenChange` undefined; callers that drive open from an
   * external trigger (DesktopTopbar's avatar at ≥1280 px) pass both.
   */
  open?: boolean;
  /** See `open` — fires on every open-state transition. */
  onOpenChange?: (open: boolean) => void;
  /**
   * P0 sign-out fix (2026-05-10): suppress UserMenu's internal trigger button.
   * Used on desktop where DesktopTopbar's avatar is the visible trigger
   * (Wave 4 W4.2 `data-testid="user-menu-trigger"`); the menu still owns the
   * dropdown panel but renders no trigger of its own. Default: false.
   */
  hideTrigger?: boolean;
  /**
   * P0 sign-out fix (2026-05-10): panel positioning variant.
   *  - "anchored" (default): the legacy `position: absolute` panel anchored to
   *    UserMenu's trigger button (TopBar mount at <1280 px).
   *  - "fixed-top-right": `position: fixed` panel anchored to the viewport's
   *    top-right corner — used when the visible trigger lives in DesktopTopbar
   *    (an unrelated DOM ancestor) and absolute positioning relative to a
   *    hidden/empty UserMenu container would land in the wrong place.
   */
  panelPosition?: "anchored" | "fixed-top-right";
}

// ── CSS ───────────────────────────────────────────────────────────────────────

const USER_MENU_CSS = `
  /* ── Trigger button ────────────────────────────────────────────────── */

  .proof-user-menu {
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  .proof-user-menu__trigger {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    height: 30px;
    padding: 0 var(--space-2);
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    background: none;
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
    cursor: pointer;
    transition: color var(--motion-state), border-color var(--motion-state),
      background var(--motion-state);
    -webkit-tap-highlight-color: transparent;
    white-space: nowrap;
    min-width: var(--tap-target-min, 44px);
    min-height: var(--tap-target-min, 44px);
  }

  .proof-user-menu__trigger:hover {
    border-color: color-mix(in srgb, var(--text) 20%, var(--line));
  }

  /* Open state: 1px accent bottom border instead of full border, per one-accent rule */
  .proof-user-menu__trigger[aria-expanded="true"] {
    border-color: var(--accent);
    color: var(--text);
  }

  /* Mobile: collapse to circular icon button only */
  @media (max-width: 767px) {
    .proof-user-menu__trigger {
      width: 36px;
      height: 36px;
      min-width: 44px;
      min-height: 44px;
      padding: 0;
      border-radius: 50%;
      justify-content: center;
    }
  }

  /* Desktop: show name + chevron */
  .proof-user-menu__name {
    display: inline;
  }

  .proof-user-menu__chevron {
    display: inline-flex;
    align-items: center;
    color: var(--muted);
    transition: transform var(--motion-state);
    flex-shrink: 0;
  }

  .proof-user-menu__trigger[aria-expanded="true"] .proof-user-menu__chevron {
    transform: rotate(180deg);
  }

  /* Mobile: hide name + chevron, show user icon */
  .proof-user-menu__icon-mobile {
    display: none;
  }

  @media (max-width: 767px) {
    .proof-user-menu__name,
    .proof-user-menu__chevron {
      display: none;
    }

    .proof-user-menu__icon-mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--muted);
    }

    .proof-user-menu__trigger[aria-expanded="true"] .proof-user-menu__icon-mobile {
      color: var(--text);
    }
  }

  /* ── Dropdown panel (desktop) ──────────────────────────────────────── */

  .proof-user-menu__panel {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-card, 6px);
    z-index: 200;
    overflow: hidden;
    /* No shadow per hairlines-over-shadows rule (design.md §2). */
  }

  /* P0 sign-out fix (2026-05-10): "fixed-top-right" variant pins the panel to
     the viewport's top-right corner — used when the visible trigger lives in
     DesktopTopbar (an unrelated DOM ancestor) and absolute positioning
     relative to a hidden UserMenu container would land in the wrong place.
     DesktopTopbar height is 52 px (DesktopTopbar.css line 24); the avatar
     sits flush right with the bar's 24 px padding (DesktopTopbar.css line 25).
     Top: 58 px (52 + 6 px gap mirrors the anchored variant). Right: 24 px
     mirrors DesktopTopbar's right padding so the panel's right edge lines up
     with the avatar's right edge. */
  .proof-user-menu__panel--fixed-top-right {
    position: fixed;
    top: 58px;
    right: 24px;
  }

  @media (max-width: 767px) {
    .proof-user-menu__panel {
      display: none;
    }
  }

  /* ── Menu identity row ─────────────────────────────────────────────── */

  .proof-user-menu__identity {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--line);
  }

  .proof-user-menu__identity-name {
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .proof-user-menu__identity-email {
    font-family: var(--font-sans);
    font-size: 11px;
    color: var(--faint);
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Menu items ─────────────────────────────────────────────────────── */

  .proof-user-menu__items {
    display: flex;
    flex-direction: column;
    padding: var(--space-1) 0;
  }

  .proof-user-menu__item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-2) var(--space-4);
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 400;
    color: var(--text);
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background var(--motion-state);
    -webkit-tap-highlight-color: transparent;
    /* 44px touch floor by default (Rule 7 / WCAG 2.5.5). The desktop popover
       keeps its 36px density for POINTER users only, via the (pointer: fine)
       step-down below — so a 768px touch tablet (pointer: coarse) floors to
       44px instead of inheriting the sub-floor 36px it used to. Same
       pointer-keyed density idiom as SaveAsVersionButton / ScalePill (E2). */
    min-height: var(--tap-target-min, 44px);
    gap: var(--space-3);
  }

  /* Row leading icon — 20×20 box, currentColor stroke. Frame 8 / Frame 22.
     Mobile sheet: muted by default; matches UserMenu mobile Sheet rowIconStyle. */
  .proof-user-menu__item-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: var(--muted);
  }
  .proof-user-menu__item--signout .proof-user-menu__item-icon {
    color: var(--del, #c03a24);
  }
  .proof-user-menu__item--disabled .proof-user-menu__item-icon {
    color: var(--faint);
  }

  /* Row label fills the remaining space so the chevron pins to the right. */
  .proof-user-menu__item-label {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Trailing chevron — text glyph, faint colour. Mirrors UserMenu mobile Sheet chevron.
     Hidden on the destructive sign-out row (Frame 8). */
  .proof-user-menu__item-chevron {
    color: var(--faint, rgba(0,0,0,0.25));
    flex-shrink: 0;
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1;
  }

  .proof-user-menu__item:hover {
    background: color-mix(in srgb, var(--text) 4%, transparent);
  }

  /* Destructive: sign out uses var(--text) not semantic red (design.md §2). */
  .proof-user-menu__item--signout {
    color: var(--muted);
    border-top: 1px solid var(--line);
    margin-top: var(--space-1);
  }

  .proof-user-menu__item--signout:hover {
    color: var(--text);
    background: color-mix(in srgb, var(--text) 4%, transparent);
  }

  /* Disabled placeholder row — e.g. "Public pages — Coming soon."
     UX fixes wave 1 (2026-05-04): replaces the removed TopBar Workspace/Share toggle.
     Not interactive; keyboard-focusable for accessibility (aria-disabled). */
  .proof-user-menu__item--disabled {
    color: var(--faint);
    cursor: default;
    pointer-events: none;
  }

  .proof-user-menu__item--disabled:hover {
    background: none;
  }

  /* Mobile sheet: bump up tap targets */
  @media (max-width: 767px) {
    .proof-user-menu__item {
      min-height: 48px;
      padding: var(--space-3) var(--space-4);
      font-size: 15px;
    }

    .proof-user-menu__identity {
      padding: var(--space-4);
    }
  }

  /* Desktop popover density: step the row height down to 36px for POINTER users
     only. Touch tablets at ≥ 768px (pointer: coarse) keep the 44px base floor
     above — this preserves desktop density without dropping the touch target on
     a 768px iPad (design.md §14.5, E2 pointer-keyed idiom). */
  @media (min-width: 768px) and (pointer: fine) {
    .proof-user-menu__item {
      min-height: 36px;
    }
  }
`;

// ── Icons ─────────────────────────────────────────────────────────────────────

// 16×16 SVG glyphs, 1.5px stroke, currentColor. Internal — not user-visible names.

function IconChevronDown() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2 4l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2 13.5c0-2.485 2.686-4.5 6-4.5s6 2.015 6 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Row icons (Frame 8 / Frame 22) ────────────────────────────────────────────
//
// 20×20 SVGs, 1.5px stroke, currentColor. Shared icon set for both the
// desktop dropdown and the mobile Sheet so both surfaces use the same visual vocabulary. Each row
// in the menu carries one of these in a leading 20×20 slot (.proof-user-menu__
// item-icon) followed by the label and trailing chevron (Frame 8). Without
// these the live mobile sheet renders icon-less rows — the bug captured in
// tasks/design-vs-live-2026-05-08/shots/live-MOBILE-ACCOUNT-SHEET.png.

function IconRowPerson() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3 17c0-3 3.134-5.5 7-5.5s7 2.5 7 5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconRowGroups() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7" cy="7.5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2.5 16c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="13.5" cy="6" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M13 12c2.2 0 4 1.6 4 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconRowFeed() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      {/* Stacked cards — the feed / stream motif (matches the topbar Feed icon). */}
      <rect x="3" y="3.5" width="14" height="4" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <rect x="3" y="10" width="14" height="4" rx="1.25" stroke="currentColor" strokeWidth="1.5" />
      <line
        x1="3"
        y1="16.5"
        x2="12"
        y2="16.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Settings — a GEAR (toothed ring + inner hole). design.md §2 principle 8(a)
// bans circle-plus-radial-spokes for Settings: it reads as a sun and users take
// it for a theme toggle (2026-04-27 QA). §6.2 specifies the gear.
function IconRowGear() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M8.6 2.6h2.8l.32 1.86a6.1 6.1 0 0 1 1.44.84l1.77-.66 1.4 2.42-1.45 1.2a6.15 6.15 0 0 1 0 1.68l1.45 1.2-1.4 2.42-1.77-.66c-.44.35-.92.63-1.44.84L11.4 17.4H8.6l-.32-1.86a6.1 6.1 0 0 1-1.44-.84l-1.77.66-1.4-2.42 1.45-1.2a6.15 6.15 0 0 1 0-1.68l-1.45-1.2 1.4-2.42 1.77.66c.44-.35.92-.63 1.44-.84L8.6 2.6Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="2.35" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function IconRowCode() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <polyline
        points="7,6 3,10 7,14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points="13,6 17,10 13,14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRowExport() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 3v10M6 9l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconRowBook() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 3h7a2 2 0 0 1 2 2v11a2 2 0 0 0-2-2H4V3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M13 16a2 2 0 0 1 2-2h1V5a2 2 0 0 0-2-2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRowFeedback() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M4 4h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6l-3 3V5a1 1 0 0 1 1-1z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRowAdmin() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 2L4 5v5c0 3.5 2.5 6.5 6 7.5 3.5-1 6-4 6-7.5V5l-6-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRowSignOut() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M13 10h5M16 8l2 2-2 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 3H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Panel content (shared between dropdown and sheet) ─────────────────────────

interface PanelContentProps {
  firstName: string;
  email: string;
  /** PR12: top action row; optional so demo mode can omit it. */
  onOpenProfile?: () => void;
  onOpenSettings: () => void;
  /** Groups surface (family-groups). Row rendered only when provided. */
  onOpenGroups?: () => void;
  /** Feed surface (B-WP11b). Row rendered only when provided. */
  onOpenFeed?: () => void;
  onOpenCustomLogic: () => void;
  onOpenExports: () => void;
  /** Syntax help row — navigate("/app/help"). */
  onOpenHelp: () => void;
  /** Optional Feedback row — see UserMenuProps.onOpenFeedback. */
  onOpenFeedback?: () => void;
  onSignOut: () => void;
  /**
   * Close the menu.
   *
   * #221 (mobile row actions): may carry a `pendingAction` to run AFTER the
   * menu has closed. On mobile the menu is a Vaul Drawer that the parent
   * unmounts on close (`{open && !isDesktop && <Sheet>}`). The old row handler
   * ran the action and closed in the same click, which unmounted the Drawer
   * mid-event — the row's click never completed through the portal-and-unmount
   * race, so the action was silently dropped. Settings / Send feedback /
   * Profile (which only mutate app state or client-navigate) no-op'd on mobile;
   * Sign out survived only because it does a hard `window.location` redirect.
   * Desktop (inline panel, no portal, no animated unmount) was unaffected.
   * Passing the action to `onClose` lets the parent defer it until after the
   * close settles. A bare `onClose()` (× button / scrim) still works.
   */
  onClose: (pendingAction?: () => void) => void;
  /** Rendered only when isAdmin is true. */
  isAdmin: boolean;
  onOpenAdmin?: () => void;
}

function PanelContent({
  firstName,
  email,
  onOpenProfile,
  onOpenSettings,
  onOpenGroups,
  onOpenFeed,
  onOpenCustomLogic,
  onOpenExports,
  onOpenHelp,
  onOpenFeedback,
  onSignOut,
  onClose,
  isAdmin,
  onOpenAdmin,
}: PanelContentProps) {
  // #221: hand the action to the parent via onClose, which closes the menu
  // first and runs the action AFTER the close settles. Do NOT run `cb()` then
  // close inline — on mobile that unmounts the Vaul Drawer mid-click and the
  // action is lost.
  function handleItem(cb: () => void) {
    onClose(cb);
  }

  return (
    <>
      {/* Identity row */}
      <div className="proof-user-menu__identity">
        <span className="proof-user-menu__identity-name">{firstName}</span>
        <span className="proof-user-menu__identity-email">{email}</span>
      </div>

      {/* Action items.
          Frame 8 (MOBILE-ACCOUNT-SHEET) + Frame 22 (DESKTOP-ACCOUNT-DROPDOWN):
          each row carries a 20×20 leading icon, a flex-1 label, and (except
          the destructive Sign out row) a trailing chevron. Without the icon
          slot the mobile sheet renders icon-less rows — the bug captured in
          tasks/design-vs-live-2026-05-08/shots/live-MOBILE-ACCOUNT-SHEET.png. */}
      <div className="proof-user-menu__items" role="menu">
        {/* PR12 (N2=B): Profile is the TOP row — above Settings / Sign out / Feedback.
            Discoverability rule: must have a labelled entry on every breakpoint. */}
        {onOpenProfile !== undefined && (
          <button
            type="button"
            className="proof-user-menu__item"
            role="menuitem"
            onClick={() => handleItem(onOpenProfile)}
            data-testid="user-menu-profile"
          >
            <span className="proof-user-menu__item-icon" aria-hidden="true">
              <IconRowPerson />
            </span>
            <span className="proof-user-menu__item-label">Profile</span>
            <span className="proof-user-menu__item-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        )}
        <button
          type="button"
          className="proof-user-menu__item"
          role="menuitem"
          onClick={() => handleItem(onOpenSettings)}
          data-testid="user-menu-settings"
        >
          <span className="proof-user-menu__item-icon" aria-hidden="true">
            <IconRowGear />
          </span>
          <span className="proof-user-menu__item-label">Settings</span>
          <span className="proof-user-menu__item-chevron" aria-hidden="true">
            ›
          </span>
        </button>

        {/* Groups (family-groups, plan 2026-07-02 §4.5) — labelled primary-nav
            entry so the surface is discoverable on every breakpoint (design.md
            §6.0a). Rendered only when the shell wires onOpenGroups. */}
        {onOpenGroups !== undefined && (
          <button
            type="button"
            className="proof-user-menu__item"
            role="menuitem"
            onClick={() => handleItem(onOpenGroups)}
            data-testid="user-menu-groups"
          >
            <span className="proof-user-menu__item-icon" aria-hidden="true">
              <IconRowGroups />
            </span>
            <span className="proof-user-menu__item-label">Groups</span>
            <span className="proof-user-menu__item-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        )}

        {/* Feed (B-WP11b) — labelled primary-nav entry so the Feed is
            discoverable on every breakpoint (design.md §6.0a). This is the
            <768px half of the pair; the DesktopTopbar "Feed" control is the
            ≥768px half. Rendered only when the shell wires onOpenFeed. */}
        {onOpenFeed !== undefined && (
          <button
            type="button"
            className="proof-user-menu__item"
            role="menuitem"
            onClick={() => handleItem(onOpenFeed)}
            data-testid="user-menu-feed"
          >
            <span className="proof-user-menu__item-icon" aria-hidden="true">
              <IconRowFeed />
            </span>
            <span className="proof-user-menu__item-label">Feed</span>
            <span className="proof-user-menu__item-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        )}

        <button
          type="button"
          className="proof-user-menu__item"
          role="menuitem"
          onClick={() => handleItem(onOpenCustomLogic)}
          data-testid="user-menu-custom-logic"
        >
          <span className="proof-user-menu__item-icon" aria-hidden="true">
            <IconRowCode />
          </span>
          <span className="proof-user-menu__item-label">Custom logic</span>
          <span className="proof-user-menu__item-chevron" aria-hidden="true">
            ›
          </span>
        </button>

        <button
          type="button"
          className="proof-user-menu__item"
          role="menuitem"
          onClick={() => handleItem(onOpenExports)}
          data-testid="user-menu-exports"
        >
          <span className="proof-user-menu__item-icon" aria-hidden="true">
            <IconRowExport />
          </span>
          <span className="proof-user-menu__item-label">Exports</span>
          <span className="proof-user-menu__item-chevron" aria-hidden="true">
            ›
          </span>
        </button>

        {/* Syntax help — between Exports and Send feedback.
            Plan: .plan/features/2026-06-12-help-syntax-page.md §D3.
            Row order: Profile / Settings / Custom logic / Exports /
            Syntax help / Send feedback / (Admin) / Sign out. */}
        <button
          type="button"
          className="proof-user-menu__item"
          role="menuitem"
          onClick={() => handleItem(onOpenHelp)}
          data-testid="user-menu-help"
        >
          <span className="proof-user-menu__item-icon" aria-hidden="true">
            <IconRowBook />
          </span>
          <span className="proof-user-menu__item-label">Syntax help</span>
          <span className="proof-user-menu__item-chevron" aria-hidden="true">
            ›
          </span>
        </button>

        {/* Frame 8 row order: Profile / Settings / Custom logic / Exports /
            Syntax help / Send feedback / (Admin) / Sign out. The "Public pages — Coming soon."
            placeholder + the L/D/S Theme toggle were removed 2026-05-08 to
            match the locked design — theme moves to Settings; public pages
            remains deferred but no longer surfaces as a chrome row.
            Plan ref: design-vs-live delta sweep, 2026-05-08.

            Send feedback (Frame 8) — between Exports and Sign out. The label
            is "Send feedback" per Frame 8; we keep the
            existing onOpenFeedback prop name so callers don't churn. */}
        {onOpenFeedback !== undefined && (
          <button
            type="button"
            className="proof-user-menu__item"
            role="menuitem"
            onClick={() => handleItem(onOpenFeedback)}
            data-testid="user-menu-feedback"
          >
            <span className="proof-user-menu__item-icon" aria-hidden="true">
              <IconRowFeedback />
            </span>
            <span className="proof-user-menu__item-label">Send feedback</span>
            <span className="proof-user-menu__item-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        )}

        {isAdmin && onOpenAdmin !== undefined && (
          <button
            type="button"
            className="proof-user-menu__item"
            role="menuitem"
            onClick={() => handleItem(onOpenAdmin)}
            data-testid="user-menu-admin"
          >
            <span className="proof-user-menu__item-icon" aria-hidden="true">
              <IconRowAdmin />
            </span>
            <span className="proof-user-menu__item-label">Admin</span>
            <span className="proof-user-menu__item-chevron" aria-hidden="true">
              ›
            </span>
          </button>
        )}

        <button
          type="button"
          className="proof-user-menu__item proof-user-menu__item--signout"
          role="menuitem"
          onClick={() => handleItem(onSignOut)}
          data-testid="user-menu-sign-out"
        >
          <span className="proof-user-menu__item-icon" aria-hidden="true">
            <IconRowSignOut />
          </span>
          <span className="proof-user-menu__item-label">Sign out</span>
        </button>
      </div>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function UserMenu({
  firstName,
  email,
  onOpenProfile,
  onOpenSettings,
  onOpenGroups,
  onOpenFeed,
  onSetTheme: _onSetTheme, // forwarded via prop contract; internal theme is managed by useTheme
  onOpenCustomLogic,
  onOpenExports,
  onOpenHelp,
  onOpenFeedback,
  onSignOut,
  onOpenAdmin,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  panelPosition = "anchored",
}: UserMenuProps) {
  // Controlled-vs-uncontrolled open state.
  // When `controlledOpen` is provided the parent owns the open state (e.g. the
  // DesktopTopbar mount drives open from its avatar click). Otherwise the
  // component owns its own internal state (the legacy TopBar mount).
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean | ((v: boolean) => boolean)) => {
    const resolved = typeof next === "function" ? next(open) : next;
    if (!isControlled) setInternalOpen(resolved);
    onOpenChange?.(resolved);
  };
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // #221 (mobile row actions): defer a selected row's action until AFTER the
  // menu has closed. On mobile the menu is a Vaul Drawer that this component
  // unmounts on close (`{open && !isDesktop && <Sheet>}`). Running the action
  // and closing in the same click unmounts the Drawer mid-event and the action
  // is dropped — proven via an instrumented jsdom repro (real UserMenu + real
  // @proof/ui Sheet, wired like app.tsx): a row click yields 0 callback
  // invocations on the mobile Sheet vs 1 on the desktop inline panel. Stashing
  // the action and flushing it after close avoids the in-event unmount race
  // entirely; the actions (client-navigate / open a route-level surface) never
  // depend on the Sheet still existing, so running them post-close is safe. One
  // structural path, identical on every device — fixing Settings + Send
  // feedback + Profile together (Sign out already worked via a hard
  // window.location redirect).
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Close the menu, optionally stashing an action to run once `open` flips to
  // false (see the flush effect below). The × button / scrim pass no action.
  const closeMenu = (pendingAction?: () => void) => {
    pendingActionRef.current = pendingAction ?? null;
    setOpen(false);
  };

  // Flush the pending action on the open → false transition. By the time this
  // effect runs, React has committed the close (the Sheet / dropdown panel is
  // unmounted), so the action runs in a clean tick with no live Drawer to race
  // against. This never depends on a Vaul / Radix animation event arriving, so
  // it behaves identically on every device.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (wasOpen && !open && pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action();
    }
  }, [open]);

  // Admin detection — fires once per mount. Defaults to false until admin.ping
  // resolves. Silent on failure (passive detection, not a user action).
  const isAdmin = useIsAdmin();

  // Viewport detection — used to suppress the mobile Sheet on desktop.
  // At ≥768px, the dropdown panel handles all interactions; mounting the Sheet
  // (a Radix Dialog) alongside the dropdown causes the Dialog's DismissableLayer
  // to set body { pointer-events: none } and the scrim to cover the dropdown,
  // swallowing clicks on action rows inside the dropdown (golden-path step 8
  // regression introduced in PR #165). The Sheet is only ever needed on mobile.
  const isDesktop = useIsDesktop();

  // Inject CSS once.
  useEffect(() => {
    const id = "proof-user-menu-css";
    if (typeof document !== "undefined" && document.getElementById(id) === null) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = USER_MENU_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // Close on outside click (panel or trigger).
  // P0 sign-out fix (2026-05-10): when `hideTrigger` is set the visible trigger
  // lives outside UserMenu's subtree (DesktopTopbar's avatar), so triggerRef is
  // null. Treat any element matching `[data-testid='user-menu-trigger']` as a
  // legal "inside" click so toggling the external avatar doesn't immediately
  // close the panel via this outside-click handler.
  //
  // #221 deep fix (2026-05-29): the prior pendingActionRef fix (dba085f)
  // correctly deferred row actions to AFTER the Sheet closes, but failed
  // because the outside-click handler (document pointerdown) fires BEFORE the
  // row button's click event. When the target is inside the Vaul Drawer portal,
  // `panelRef.current` is null (the desktop dropdown panel isn't rendered on
  // mobile), so the handler called setOpen(false) immediately. React commits
  // the unmount synchronously after handlePointerDown returns — the Vaul portal
  // tears down and the row button no longer exists in the DOM, so the click
  // event is cancelled. handleItem / onClose(pendingAction) never fires, and
  // the pending action is never stored. Three of the five #221 items failed for
  // this reason (d2-feedback, d2-profile, d3-settings) while d1 (sheet open)
  // and d5 (scroll) were unaffected.
  //
  // Fix: exclude targets inside .proof-sheet-panel (the Vaul Drawer Content
  // class set by Sheet.css). The class is more reliable than data-testid
  // because it is always present regardless of the detent prop. When the
  // target is inside the Sheet, we return early so the click event can
  // complete: handleItem fires, onClose(cb) stores the pending action, and
  // the prevOpenRef effect runs the action after the Sheet unmounts.
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-testid="user-menu-trigger"]')) {
        return;
      }
      // Mobile Sheet (Vaul portal): exclude clicks inside the Sheet content so
      // the row's click event completes before the Sheet is torn down.
      if (target instanceof Element && target.closest(".proof-sheet-panel")) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  // The L/D/S theme toggle moved to Settings (Frame 8 lock 2026-05-08).
  // The `onSetTheme` prop is kept on UserMenuProps so callers don't churn,
  // but it is no longer wired internally — the useTheme hook is unmounted
  // here. Theme writes happen in the dedicated Settings surface now.

  return (
    <div className="proof-user-menu" data-testid="user-menu">
      {/* Trigger.
          P0 sign-out fix (2026-05-10): suppressed when `hideTrigger` is set —
          DesktopTopbar's avatar is the visible trigger at ≥1280 px and carries
          the `data-testid="user-menu-trigger"` testid (Wave 4 W4.2). UserMenu
          still owns the dropdown panel + outside-click + Escape handling. */}
      {!hideTrigger && (
        <button
          ref={triggerRef}
          type="button"
          className="proof-user-menu__trigger"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={`Account menu for ${firstName}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="user-menu-trigger"
        >
          {/* Mobile: user icon only */}
          <span className="proof-user-menu__icon-mobile">
            <IconUser />
          </span>

          {/* Desktop: first name + chevron */}
          <span className="proof-user-menu__name" data-testid="user-menu-name">
            {firstName}
          </span>
          <span className="proof-user-menu__chevron">
            <IconChevronDown />
          </span>
        </button>
      )}

      {/* Desktop dropdown panel.
          panelPosition="fixed-top-right" (2026-05-10): adds the
          `--fixed-top-right` modifier when the visible trigger lives in
          DesktopTopbar (an unrelated DOM ancestor). See CSS comment block
          on `.proof-user-menu__panel--fixed-top-right`.

          P0-4 follow-up (2026-05-27, bug #221 residual): gate on `isDesktop`
          so the panel does not mount at all on mobile viewports. Previously
          only CSS hid it (`@media (max-width: 767px) { display: none }`), so
          Playwright `.first()` selectors at 375 px resolved into the hidden
          panel instead of the visible mobile sheet — 10 specs across
          `discoverability.spec.ts` + `account-menu-discoverable.spec.ts`
          failed because their `[data-testid='user-menu-trigger']` /
          `user-menu-*` queries hit the dead-panel copy first.
          The mobile Sheet below carries `{open && !isDesktop && ...}` —
          this gate mirrors it so the document never contains both at once. */}
      {open && isDesktop && (
        <div
          ref={panelRef}
          className={
            panelPosition === "fixed-top-right"
              ? "proof-user-menu__panel proof-user-menu__panel--fixed-top-right"
              : "proof-user-menu__panel"
          }
          role="menu"
          aria-label="Account menu"
          data-testid="user-menu-panel"
        >
          <PanelContent
            firstName={firstName}
            email={email}
            {...(onOpenProfile !== undefined ? { onOpenProfile } : {})}
            onOpenSettings={onOpenSettings}
            {...(onOpenGroups !== undefined ? { onOpenGroups } : {})}
            {...(onOpenFeed !== undefined ? { onOpenFeed } : {})}
            onOpenCustomLogic={onOpenCustomLogic}
            onOpenExports={onOpenExports}
            onOpenHelp={onOpenHelp}
            {...(onOpenFeedback !== undefined ? { onOpenFeedback } : {})}
            onSignOut={onSignOut}
            onClose={closeMenu}
            isAdmin={isAdmin}
            {...(onOpenAdmin !== undefined ? { onOpenAdmin } : {})}
          />
        </div>
      )}

      {/* Mobile bottom sheet — UserMenu mobile Sheet. Row order:
          Profile → Settings → Groups → Custom logic → Exports → Syntax help →
          Send feedback →
          (Admin) → Sign out.
          Composed on @proof/ui Sheet primitive (W4.1) — safe-area inset,
          Safari layout-viewport anchor, glass treatment, and reduced-motion
          all handled by the primitive. Desktop renders via the dropdown panel
          above; Sheet is only mounted on mobile (<768px) — see isDesktop guard
          below. Mounting the Sheet at ≥768px causes the Radix Dialog's
          DismissableLayer to set body { pointer-events: none } and the scrim to
          cover the dropdown, swallowing action-row clicks (golden-path step 8
          regression, PR #165 → fixed here). */}
      {open && !isDesktop && (
        <Sheet
          detent="medium"
          onDismiss={() => setOpen(false)}
          aria-label="Account"
          data-testid="user-menu-sheet"
        >
          <SheetHeader title="Account" onDismiss={() => setOpen(false)} />
          {/*
           * data-vaul-no-drag: tells Vaul not to treat touch gestures that
           * start inside this div as drag-to-dismiss candidates. Without it,
           * Vaul's shouldDrag() walks up from the touch target, finds
           * .proof-sheet-body has scrollHeight > clientHeight (content overflows)
           * but scrollTop === 0 (user is at top of list), continues up to the
           * panel element, finds scrollHeight === clientHeight there (flex
           * layout fills exactly), and never reaches the role="dialog" bail-out
           * — so it returns true (allow drag) and dismisses the sheet instead
           * of scrolling the list.
           *
           * With data-vaul-no-drag, Vaul returns false immediately (line 981
           * of vaul/dist/index.mjs), letting the browser's native scroll
           * handle the gesture. The sheet is still dismissible via the handle,
           * the × button, and the scrim tap.
           *
           * overflowY + min-height: 0 are already in .proof-sheet-body CSS
           * (Sheet.css lines 356-361); the inline style only carries the glass
           * tint that is UserMenu-specific.
           */}
          <div
            className="proof-sheet-body"
            data-vaul-no-drag
            style={{
              background: "var(--glass-tint-strong, var(--bg))",
              backdropFilter: "blur(var(--glass-blur, 16px)) saturate(140%)",
              WebkitBackdropFilter: "blur(var(--glass-blur, 16px)) saturate(140%)",
              borderTop: "1px solid var(--glass-border, var(--line))",
              boxShadow: "var(--glass-shadow, none)",
            }}
          >
            <PanelContent
              firstName={firstName}
              email={email}
              {...(onOpenProfile !== undefined ? { onOpenProfile } : {})}
              onOpenSettings={onOpenSettings}
              // Groups must reach the mobile sheet too — omitting it here hid
              // the row entirely below 768px (dev verification 2026-07-02,
              // BUG-2; design.md §6.0a: labelled primary-nav entry on EVERY
              // breakpoint).
              {...(onOpenGroups !== undefined ? { onOpenGroups } : {})}
              {...(onOpenFeed !== undefined ? { onOpenFeed } : {})}
              onOpenCustomLogic={onOpenCustomLogic}
              onOpenExports={onOpenExports}
              onOpenHelp={onOpenHelp}
              {...(onOpenFeedback !== undefined ? { onOpenFeedback } : {})}
              onSignOut={onSignOut}
              onClose={closeMenu}
              isAdmin={isAdmin}
              {...(onOpenAdmin !== undefined ? { onOpenAdmin } : {})}
            />
          </div>
        </Sheet>
      )}
    </div>
  );
}

// ── Utility: derive first name from account fields ────────────────────────────

/**
 * Derives the display first name from account fields.
 *
 * Priority:
 *   1. Split `fullName` on the first space; take the leading token.
 *   2. If `fullName` is null/empty, fall back to the part of `email` before `@`.
 */
export function deriveFirstName(fullName: string | null | undefined, email: string): string {
  if (fullName && fullName.trim().length > 0) {
    return fullName.trim().split(/\s+/)[0] ?? fullName.trim();
  }
  // Fallback: email local-part
  return email.split("@")[0] ?? email;
}

/**
 * Derives the user-menu trigger label from account fields.
 *
 * Locked design (operator, 2026-05-03 PH2):
 *   "we should change it to use the handle if it is set."
 *
 * Priority:
 *   1. Public-slug `handle` (with `@` prefix per branding.md §8 vocabulary —
 *      `@handle` is the canonical inline form, e.g. `Spun off from @handle`).
 *   2. First token of `fullName` (the BetterAuth display name).
 *   3. Email local-part.
 *
 * The `@` prefix differentiates the slug from a display name in the trigger,
 * matching the share-mode wordmark suffix (design.md §6.1).
 */
export function deriveMenuLabel(
  handle: string | null | undefined,
  fullName: string | null | undefined,
  email: string,
): string {
  if (handle && handle.trim().length > 0) {
    return `@${handle.trim()}`;
  }
  return deriveFirstName(fullName, email);
}
